/* eslint-disable import/extensions */
/* eslint-disable import/no-unresolved */

import Promise from 'bluebird';
import $ from 'jquery';
import { cloneDeep } from 'lodash';
import { noop, extend, defer } from 'underscore';
import { FilterService } from '@bingads-webui/filter-service';
import { getDimensionsGridFactory } from '@bingads-webui-campaign/dimensions-grid';
import { WaterfallFilterStorage, WaterfallFilterType, waterfallDateFilterProcessor } from '@bingads-webui-campaign/filter-storage';
import { getDimensionsGridConfig, Constants } from '@bingads-webui-campaign/dimensions-grids-config';
import {
  DimensionAgeGender,
  DimensionCallForwarding,
  DimensionGeographic,
  DimensionLanding,
  DimensionLocation,
  DimensionPublisher,
  DimensionSearchTerm,
  DimensionShopping,
  Time,
  KeywordLabelReport,
  AdLabelReport,
  AdGroupLabelReport,
  CampaignLabelReport,
  AccountLabelMccReport,
} from '@bingads-webui-campaign/campaign-ui-schemas';

import * as edm from '@bingads-webui-component-legacy/edm-campaign/index';
import appConfig from '@bingads-webui-component-legacy/config/index';
import jsdata from '@bingads-webui-component-legacy/js-data-campaign/index';
import { bingadsGridColumn } from '@bingads-webui-component-legacy/bingads-grid/shared/column';
import cacheService from '@bingads-webui-static-legacy/cache-service';
import dataContextManager from '@bingads-webui-static-legacy/summary/SummaryDataContextManager';
import sessionStore from '@bingads-webui-component-legacy/js-data-storage/session-storage';
import * as preferenceStoreFactoryImport from '@bingads-webui-component-legacy/bingads-grid/service/filter-preferences';
import PageContext from '@bingads-webui-static-legacy/page-context';

import { dimensionsGridLegacyDepsList } from './legacy-deps-list';
import { getFilterConfig, dimensionsFilterOdataOptionsProcessor } from './filter-config';
import { getAdditionalPlugins } from './additional-plugins';


const dimensionsPromiseFactory = () => new Promise((resolve) => {
  defer(() => {
    const dependecyList = dimensionsGridLegacyDepsList;

    getDimensionsGridFactory()(dependecyList)
      .then((result) => {
        resolve(result);
      });
  });
});

function promisifyDimensionSchemaLoader(schemaName) {
  return () => new Promise((res) => {
    const schemaCopy = cloneDeep(schemaName);
    if (schemaCopy.properties.PerformanceMetrics) {
      if (schemaCopy.properties.PerformanceMetrics.schema.properties.ViewThroughConversionsCPA) {
        delete schemaCopy.properties.PerformanceMetrics.schema.properties.ViewThroughConversionsCPA;
      }

      if (schemaCopy.properties.PerformanceMetrics.schema.properties.ViewThroughConversionsRevenue) {
        delete schemaCopy.properties.PerformanceMetrics.schema.properties.ViewThroughConversionsRevenue;
      }

      if (schemaCopy.properties.PerformanceMetrics.schema.properties.ViewThroughConversionsReturnOnAdSpend) {
        delete schemaCopy.properties.PerformanceMetrics.schema.properties.ViewThroughConversionsReturnOnAdSpend;
      }

      if (schemaCopy.properties.PerformanceMetrics.schema.properties.ViewThroughConversionsRate) {
        delete schemaCopy.properties.PerformanceMetrics.schema.properties.ViewThroughConversionsRate;
      }
    }

    res(schemaCopy);
  });
}

function getLevel(scope) {
  if (scope.values.adGroupId) {
    return Constants.AdgroupLevel;
  } else if (scope.values.campaignId) {
    return Constants.CampaignLevel;
  }
  return Constants.AccountLevel;
}

export function createDimensionsGridForUiNext({
  serverContext,
  toolbarPlugin,
  preferenceService,
  scope,
  permissions,
  currentCampaign,
  currentAdGroup,
  perfMarker,
  gridName,
  gridType,
  customDataSource = null,
  willParentViewUnMount,
}) {
  return new Promise((resolve) => {
    const campaignContext = {
      currentCampaign,
      currentAdGroup,
    };

    dimensionsPromiseFactory({
      serverContext,
      scope,
      permissions,
      campaignContext,
      preferenceService,
    }).then(({ createDimensionGrid }) => {
      const i18n = window.globalLegacyI18n.get('@bingads-webui/i18n-amd-loader!campaignui/feature/dimension');
      const globali18n = window.globalLegacyI18n.get('$/i18n!legacy/global');
      const preferenceStoreFactory = preferenceStoreFactoryImport.getFilters;
      // some of dependencies, such as error mapper, need this file.
      const schemaI18nCampaign = window.globalLegacyI18n.get('$/schema-i18n!campaign');
      const legacyGlobalI18n = window.globalLegacyI18n.get('$/i18n!global');

      const schemaLoaders = {
        getAgeSchema: promisifyDimensionSchemaLoader(DimensionAgeGender),
        getCallForwardingSchema: promisifyDimensionSchemaLoader(DimensionCallForwarding),
        getGeographicSchema: promisifyDimensionSchemaLoader(DimensionGeographic),
        getLandingSchema: promisifyDimensionSchemaLoader(DimensionLanding),
        getLocationSchema: promisifyDimensionSchemaLoader(DimensionLocation),
        getPublisherSchema: promisifyDimensionSchemaLoader(DimensionPublisher),
        getSearchTermSchema: promisifyDimensionSchemaLoader(DimensionSearchTerm),
        getShoppingSchema: promisifyDimensionSchemaLoader(DimensionShopping),
        getTimeSchema: promisifyDimensionSchemaLoader(Time),
        getKeywordsLabelSchema: promisifyDimensionSchemaLoader(KeywordLabelReport),
        getAdLabelSchema: promisifyDimensionSchemaLoader(AdLabelReport),
        getAdGroupLabelSchema: promisifyDimensionSchemaLoader(AdGroupLabelReport),
        getCampaignLabelSchema: promisifyDimensionSchemaLoader(CampaignLabelReport),
        getAccountLabelSchema: promisifyDimensionSchemaLoader(AccountLabelMccReport),
      };

      getDimensionsGridConfig({
        gridType,
        legacyDeps: {
          i18n: globali18n,
          jsDataCampaign: jsdata,
          edm,
          globalConfig: appConfig,
          deleteSchema: noop,
          getCurrentLevel: () => getLevel(scope),
          bingadsGridColumn,
          pageContext: PageContext,
        },
        schemaLoaders,
        newI18n: i18n,
        isForVNext: true,
      }).then((gridConfig) => {
        if (willParentViewUnMount()) {
          resolve({});
          return;
        }

        const reportName = gridConfig.getReportName();
        const filterConfig = getFilterConfig({
          entitySchema: gridConfig.getSchema(),
          i18n,
          sessionStore,
          preferenceStoreFactory,
          legacyGlobalI18n,
          schemaI18nCampaign,
          cacheService,
          dataContextManager,
          preferenceService,
          appConfig,
          scope,
          jsdata,
          featureName: `${reportName.trim()}DimensionsGrid`,
          isForVNext: true,
          edm,
        });

        filterConfig.odataOptionsProcessor = dimensionsFilterOdataOptionsProcessor;

        const filterService = new FilterService(filterConfig);
        const waterfallFilterStorage = new WaterfallFilterStorage({
          preferenceService,
        });
        if (customDataSource) {
          // eslint-disable-next-line no-param-reassign
          customDataSource.filterSchema = filterService.entitySchema.properties;
        }

        resolve({
          filterConfig,
          filterService,
          gridView: createDimensionGrid({
            filterService,
            getAdditionalPlugins: getAdditionalPlugins({
              toolbarPlugin,
              scope,
              reportName,
              i18n,
            }),
            scope: scope.values,
            addDateChangeListener: noop,
            p$daterange: () => {
              const dateFilter = waterfallDateFilterProcessor(
                waterfallFilterStorage.getFilters([WaterfallFilterType.DateFilter]),
                serverContext
              );
              return Promise.resolve(dateFilter);
            },
            gridType,
            entitySet: gridConfig.getEntitySet(),
            startDate: '',
            endDate: '',
            isForVNext: true,
            viewport: $('.dimensions-page'),
            perfMarker,
            customHeaderOffset: () => $('.sticky-container').height(),
            preferenceConfig: {},
            campaignContext,
            gridName,
            schema: gridConfig.getSchema(),
            columnChooserConfig: extend(gridConfig.getColumnChooserConfiguration(), {
              i18n,
              columns: gridConfig.getColumns(),
            }),
            reportName,
            customDataSource,
            reportSchema: gridConfig.getReportSchema(),
            preferenceService,
            appConfig,
          }),
          i18n: legacyGlobalI18n,
          appConfig,
        });
      });
    });
  });
}
